import React, { useMemo, useState } from 'react';
import { Shield, ShieldCheck, Key, Smartphone, LogOut, AlertTriangle, CheckCircle2, XCircle, Eye, EyeOff, Monitor, Bell, Download, Copy, Lock } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useToast } from '../ui/Toast';
import { ZodiacTheme } from './zodiacThemes';
import { User } from '../../types';
import { useLanguage } from '../../i18n/LanguageContext';
interface SecurityTabProps {
  theme: ZodiacTheme;
  user: User | null;
}
const BACKUP_CODES = ['A7K2-M9X1', 'B3P8-N4Q6', 'C5R1-L7W3', 'D9T4-J2Y8', 'E1V6-H5Z0', 'F8U3-G6S7'];
const SESSIONS = [{
  id: 1,
  device: 'Mac OS',
  browser: 'Safari',
  city: 'Москва',
  country: 'Россия',
  ip: '192.168.1.1',
  time: 'Сейчас',
  current: true,
  icon: 'monitor' as const
}, {
  id: 2,
  device: 'iPhone 13',
  browser: 'Safari',
  city: 'Москва',
  country: 'Россия',
  ip: '10.0.0.5',
  time: 'Вчера, 14:30',
  current: false,
  icon: 'phone' as const
}, {
  id: 3,
  device: 'Windows 11',
  browser: 'Chrome',
  city: 'Санкт-Петербург',
  country: 'Россия',
  ip: '85.12.45.67',
  time: '3 дня назад',
  current: false,
  icon: 'monitor' as const
}];
function getPasswordStrength(pw: string, t: any): {
  level: number;
  label: string;
  color: string;
} {
  if (!pw) return {
    level: 0,
    label: '',
    color: ''
  };
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[a-zа-я]/.test(pw) && /[A-ZА-Я]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^a-zA-Zа-яА-Я0-9]/.test(pw)) score++;
  if (score <= 1) return {
    level: 1,
    label: t('userdash.security.weak'),
    color: 'bg-rose-500'
  };
  if (score === 2) return {
    level: 2,
    label: t('userdash.security.medium'),
    color: 'bg-amber-500'
  };
  if (score === 3) return {
    level: 3,
    label: t('userdash.security.good'),
    color: 'bg-emerald-400'
  };
  return {
    level: 4,
    label: t('userdash.security.strong'),
    color: 'bg-emerald-600'
  };
}
export function SecurityTab({
  theme,
  user
}: SecurityTabProps) {
  const {
    t
  } = useLanguage();
  const {
    showToast
  } = useToast();
  // Password
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  // 2FA
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [twoFAMethod, setTwoFAMethod] = useState<'sms' | 'app' | null>(null);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  // Sessions
  const [sessions, setSessions] = useState(SESSIONS);
  // Notifications
  const [notifyLogin, setNotifyLogin] = useState(true);
  const [notifyPasswordChange, setNotifyPasswordChange] = useState(true);
  const pwStrength = useMemo(() => getPasswordStrength(newPw, t), [newPw, t]);
  const pwChecks = useMemo(() => ({
    length: newPw.length >= 8,
    letters: /[a-zA-Zа-яА-Я]/.test(newPw),
    digits: /\d/.test(newPw),
    match: newPw.length > 0 && newPw === confirmPw
  }), [newPw, confirmPw]);
  // Security score
  const securityScore = useMemo(() => {
    let score = 0;
    const items = [{
      label: t('userdash.security.passwordUpdated'),
      ok: true
    }, {
      label: t('userdash.security.2faEnabled'),
      ok: is2FAEnabled
    }, {
      label: t('userdash.security.emailVerified'),
      ok: true
    }, {
      label: t('userdash.security.phoneVerified'),
      ok: false
    }];
    items.forEach(i => {
      if (i.ok) score++;
    });
    return {
      score,
      total: items.length,
      items
    };
  }, [is2FAEnabled]);
  const scorePercent = securityScore.score / securityScore.total * 100;
  const scoreColor = scorePercent >= 75 ? 'text-emerald-600' : scorePercent >= 50 ? 'text-amber-600' : 'text-rose-600';
  const scoreBg = scorePercent >= 75 ? 'bg-emerald-500' : scorePercent >= 50 ? 'bg-amber-500' : 'bg-rose-500';
  const handleChangePassword = () => {
    if (!currentPw) {
      showToast(t('userdash.security.enterCurrentPw'));
      return;
    }
    if (!pwChecks.length || !pwChecks.letters || !pwChecks.digits) {
      showToast(t('userdash.security.pwRequirements'));
      return;
    }
    if (!pwChecks.match) {
      showToast(t('userdash.security.pwMismatch'));
      return;
    }
    showToast(t('userdash.security.pwUpdated'));
    setCurrentPw('');
    setNewPw('');
    setConfirmPw('');
  };
  const handleEnable2FA = (method: 'sms' | 'app') => {
    setTwoFAMethod(method);
    setIs2FAEnabled(true);
    setShow2FASetup(false);
    showToast(t('userdash.security.2faEnabledVia', {
      method: method === 'sms' ? 'SMS' : 'Google Authenticator'
    }));
  };
  const handleEndSession = (id: number) => {
    setSessions(sessions.filter(s => s.id !== id));
    showToast(t('userdash.security.sessionEnded'));
  };
  const handleNotMe = (id: number) => {
    setSessions(sessions.filter(s => s.id !== id));
    showToast(t('userdash.security.sessionEndedNotMe'));
  };
  const handleEndAllSessions = () => {
    setSessions(sessions.filter(s => s.current));
    showToast(t('userdash.security.allSessionsEnded'));
  };
  const Toggle = ({
    checked,
    onChange
  }: {
    checked: boolean;
    onChange: (v: boolean) => void;
  }) => <button onClick={() => onChange(!checked)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 cursor-pointer ${checked ? 'bg-indigo-600' : 'bg-slate-200'}`} data-id="element-20367">
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${checked ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-20368" />
    </button>;
  const DeviceIcon = ({
    type
  }: {
    type: 'monitor' | 'phone';
  }) => type === 'monitor' ? <Monitor className="w-4 h-4" data-id="element-20369" /> : <Smartphone className="w-4 h-4" data-id="element-20370" />;
  return <div className="space-y-5 max-w-3xl pb-8" data-id="element-20371">
      {/* Header */}
      <div data-id="element-20372">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-20373">
          {t('userdash.security.title')}
        </h2>
        <p className="text-xs text-slate-500 mt-0.5" data-id="element-20374">
          {t('userdash.security.subtitle')}
        </p>
      </div>

      {/* Security Score */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm" data-id="element-20375">
        <div className="flex items-center justify-between mb-3" data-id="element-20376">
          <div className="flex items-center gap-2" data-id="element-20377">
            {scorePercent >= 75 ? <ShieldCheck className="w-5 h-5 text-emerald-600" data-id="element-20378" /> : <Shield className="w-5 h-5 text-amber-500" data-id="element-20379" />}
            <h3 className="font-bold text-slate-900 text-sm" data-id="element-20380">
              {t('userdash.security.status')}
            </h3>
          </div>
          <span className={`text-lg font-bold ${scoreColor}`} data-id="element-20381">
            {securityScore.score}/{securityScore.total}
          </span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-2 mb-3" data-id="element-20382">
          <div className={`h-2 rounded-full transition-all ${scoreBg}`} style={{
          width: `${scorePercent}%`
        }} data-id="element-20383" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-id="element-20384">
          {securityScore.items.map((item, i) => <div key={i} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium ${item.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-20385">
              {item.ok ? <CheckCircle2 className="w-3 h-3 shrink-0" data-id="element-20386" /> : <XCircle className="w-3 h-3 shrink-0" data-id="element-20387" />}
              <span className="truncate" data-id="element-20388">{item.label}</span>
            </div>)}
        </div>
      </div>

      {/* Password Change */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm space-y-4" data-id="element-20389">
        <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2" data-id="element-20390">
          <Key className="w-4 h-4 text-slate-400" data-id="element-20391" />{' '}
          {t('userdash.security.changePassword')}
        </h3>

        <div data-id="element-20392">
          <label className="text-xs font-medium text-slate-700 mb-1 block" data-id="element-20393">
            {t('userdash.security.currentPassword')}
          </label>
          <div className="relative" data-id="element-20394">
            <input type={showCurrentPw ? 'text' : 'password'} value={currentPw} onChange={e => setCurrentPw(e.target.value)} className="w-full px-3 py-2 pr-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500" data-id="element-20395" />
            <button onClick={() => setShowCurrentPw(!showCurrentPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-20396">
              {showCurrentPw ? <EyeOff className="w-4 h-4" data-id="element-20397" /> : <Eye className="w-4 h-4" data-id="element-20398" />}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-20399">
          <div data-id="element-20400">
            <label className="text-xs font-medium text-slate-700 mb-1 block" data-id="element-20401">
              {t('userdash.security.newPassword')}
            </label>
            <div className="relative" data-id="element-20402">
              <input type={showNewPw ? 'text' : 'password'} value={newPw} onChange={e => setNewPw(e.target.value)} className="w-full px-3 py-2 pr-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500" data-id="element-20403" />
              <button onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-20404">
                {showNewPw ? <EyeOff className="w-4 h-4" data-id="element-20405" /> : <Eye className="w-4 h-4" data-id="element-20406" />}
              </button>
            </div>
          </div>
          <div data-id="element-20407">
            <label className="text-xs font-medium text-slate-700 mb-1 block" data-id="element-20408">
              {t('userdash.security.confirmPassword')}
            </label>
            <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500" data-id="element-20409" />
          </div>
        </div>

        {/* Strength indicator */}
        {newPw && <div className="space-y-2" data-id="element-20410">
            <div className="flex items-center gap-2" data-id="element-20411">
              <div className="flex gap-1 flex-1" data-id="element-20412">
                {[1, 2, 3, 4].map(lvl => <div key={lvl} className={`h-1.5 flex-1 rounded-full transition-colors ${lvl <= pwStrength.level ? pwStrength.color : 'bg-slate-100'}`} data-id="element-20413" />)}
              </div>
              <span className={`text-[10px] font-medium ${pwStrength.level <= 1 ? 'text-rose-600' : pwStrength.level === 2 ? 'text-amber-600' : 'text-emerald-600'}`} data-id="element-20414">
                {pwStrength.label}
              </span>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1" data-id="element-20415">
              {[{
            ok: pwChecks.length,
            label: t('userdash.security.minChars')
          }, {
            ok: pwChecks.letters,
            label: t('userdash.security.letters')
          }, {
            ok: pwChecks.digits,
            label: t('userdash.security.digits')
          }, {
            ok: pwChecks.match,
            label: t('userdash.security.match')
          }].map((c, i) => <span key={i} className={`text-[10px] flex items-center gap-0.5 ${c.ok ? 'text-emerald-600' : 'text-slate-400'}`} data-id="element-20416">
                  {c.ok ? <CheckCircle2 className="w-3 h-3" data-id="element-20417" /> : <XCircle className="w-3 h-3" data-id="element-20418" />}{' '}
                  {c.label}
                </span>)}
            </div>
          </div>}

        <Button size="sm" className="text-xs" onClick={handleChangePassword} disabled={!currentPw || !pwChecks.length || !pwChecks.digits || !pwChecks.match} data-id="element-20419">
          {t('userdash.security.updatePassword')}
        </Button>
      </div>

      {/* 2FA */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm space-y-4" data-id="element-20420">
        <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2" data-id="element-20421">
          <Shield className="w-4 h-4 text-slate-400" data-id="element-20422" />{' '}
          {t('userdash.security.twoFactor')}
        </h3>

        <div className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${is2FAEnabled ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`} data-id="element-20423">
          <div data-id="element-20424">
            <div className="text-sm font-medium text-slate-900 flex items-center gap-2" data-id="element-20425">
              {is2FAEnabled ? t('userdash.security.enabled') : t('userdash.security.disabled')}
              {is2FAEnabled && twoFAMethod && <Badge variant="success" size="sm" className="text-[9px] py-0 h-4" data-id="element-20426">
                  {twoFAMethod === 'sms' ? 'SMS' : 'Authenticator'}
                </Badge>}
            </div>
            <div className="text-xs text-slate-500 mt-0.5" data-id="element-20427">
              {t('userdash.security.twoFactorDesc')}
            </div>
          </div>
          {is2FAEnabled ? <Button variant="outline" size="sm" className="text-xs shrink-0" onClick={() => {
          setIs2FAEnabled(false);
          setTwoFAMethod(null);
          showToast(t('userdash.security.2faDisabled'));
        }} data-id="element-20428">
              {t('userdash.security.disable')}
            </Button> : <Button size="sm" className="text-xs shrink-0" onClick={() => setShow2FASetup(true)} data-id="element-20429">
              {t('userdash.security.enable')}
            </Button>}
        </div>

        {/* 2FA Setup */}
        {show2FASetup && !is2FAEnabled && <div className="space-y-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200" data-id="element-20430">
            <div className="text-xs font-medium text-indigo-900" data-id="element-20431">
              {t('userdash.security.chooseMethod')}:
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-20432">
              <button onClick={() => handleEnable2FA('sms')} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-indigo-300 transition-colors text-left" data-id="element-20433">
                <Smartphone className="w-5 h-5 text-indigo-600 shrink-0" data-id="element-20434" />
                <div data-id="element-20435">
                  <div className="text-xs font-medium text-slate-900" data-id="element-20436">SMS</div>
                  <div className="text-[10px] text-slate-500" data-id="element-20437">
                    {t('userdash.security.smsDesc')}
                  </div>
                </div>
              </button>
              <button onClick={() => handleEnable2FA('app')} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-indigo-300 transition-colors text-left" data-id="element-20438">
                <Lock className="w-5 h-5 text-indigo-600 shrink-0" data-id="element-20439" />
                <div data-id="element-20440">
                  <div className="text-xs font-medium text-slate-900" data-id="element-20441">
                    Authenticator
                  </div>
                  <div className="text-[10px] text-slate-500" data-id="element-20442">
                    Google / Authy
                  </div>
                </div>
              </button>
            </div>
          </div>}

        {/* Backup Codes */}
        {is2FAEnabled && <div data-id="element-20443">
            <button onClick={() => setShowBackupCodes(!showBackupCodes)} className="text-xs font-medium text-indigo-600 hover:underline flex items-center gap-1" data-id="element-20444">
              <Key className="w-3 h-3" data-id="element-20445" />{' '}
              {showBackupCodes ? t('userdash.security.hide') : t('userdash.security.show')}{' '}
              {t('userdash.security.backupCodes')}
            </button>
            {showBackupCodes && <div className="mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2" data-id="element-20446">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5" data-id="element-20447">
                  {BACKUP_CODES.map(code => <div key={code} className="text-xs font-mono text-slate-700 bg-white px-2 py-1.5 rounded border border-slate-200 text-center" data-id="element-20448">
                      {code}
                    </div>)}
                </div>
                <div className="flex gap-2" data-id="element-20449">
                  <button onClick={() => {
              navigator.clipboard?.writeText(BACKUP_CODES.join('\n'));
              showToast(t('userdash.security.codesCopied'));
            }} className="text-[10px] text-indigo-600 hover:underline flex items-center gap-0.5" data-id="element-20450">
                    <Copy className="w-3 h-3" data-id="element-20451" /> {t('userdash.security.copy')}
                  </button>
                  <button onClick={() => showToast(t('userdash.security.codesDownloaded'))} className="text-[10px] text-indigo-600 hover:underline flex items-center gap-0.5" data-id="element-20452">
                    <Download className="w-3 h-3" data-id="element-20453" />{' '}
                    {t('userdash.security.download')}
                  </button>
                </div>
              </div>}
          </div>}
      </div>

      {/* Active Sessions */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm space-y-4" data-id="element-20454">
        <div className="flex items-center justify-between" data-id="element-20455">
          <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2" data-id="element-20456">
            <Monitor className="w-4 h-4 text-slate-400" data-id="element-20457" />{' '}
            {t('userdash.security.activeSessions')}
          </h3>
          {sessions.filter(s => !s.current).length > 0 && <Button variant="outline" size="sm" className="text-xs text-rose-600 border-rose-200 hover:bg-rose-50 font-medium" onClick={handleEndAllSessions} data-id="element-20458">
              <LogOut className="w-3.5 h-3.5 mr-1" data-id="element-20459" />{' '}
              {t('userdash.security.endAllSessions')}
            </Button>}
        </div>

        <div className="space-y-2" data-id="element-20460">
          {sessions.map(s => <div key={s.id} className={`flex items-center gap-3 p-3 rounded-lg border ${s.current ? 'border-indigo-200 bg-indigo-50/50' : 'border-slate-200'}`} data-id="element-20461">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${s.current ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-500'}`} data-id="element-20462">
                <DeviceIcon type={s.icon} data-id="element-20463" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-20464">
                <div className="text-sm font-medium text-slate-900 flex items-center gap-2" data-id="element-20465">
                  {s.device} · {s.browser}
                  {s.current && <Badge variant="success" size="sm" className="text-[9px] py-0 h-4" data-id="element-20466">
                      {t('userdash.security.currentSession')}
                    </Badge>}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-20467">
                  {s.city}, {s.country} · IP: {s.ip} · {s.time}
                </div>
              </div>
              {!s.current && <div className="flex items-center gap-1 shrink-0" data-id="element-20468">
                  <button onClick={() => handleEndSession(s.id)} className="text-[10px] text-slate-500 hover:text-rose-600 px-2 py-1 rounded hover:bg-rose-50 transition-colors" data-id="element-20469">
                    {t('userdash.security.endSession')}
                  </button>
                  <button onClick={() => handleNotMe(s.id)} className="text-[10px] text-rose-600 hover:text-rose-700 px-2 py-1 rounded bg-rose-50 hover:bg-rose-100 font-medium transition-colors" data-id="element-20470">
                    {t('userdash.security.notMe')}
                  </button>
                </div>}
            </div>)}
        </div>
      </div>

      {/* Security Notifications */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm space-y-3" data-id="element-20471">
        <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2" data-id="element-20472">
          <Bell className="w-4 h-4 text-slate-400" data-id="element-20473" />{' '}
          {t('userdash.security.notifications')}
        </h3>
        <div className="divide-y divide-slate-100" data-id="element-20474">
          <div className="flex items-center justify-between gap-3 py-2.5" data-id="element-20475">
            <div data-id="element-20476">
              <div className="text-sm font-medium text-slate-900" data-id="element-20477">
                {t('userdash.security.loginAlert')}
              </div>
              <div className="text-xs text-slate-500 mt-0.5" data-id="element-20478">
                {t('userdash.security.loginAlertDesc')}
              </div>
            </div>
            <Toggle checked={notifyLogin} onChange={setNotifyLogin} data-id="element-20479" />
          </div>
          <div className="flex items-center justify-between gap-3 py-2.5" data-id="element-20480">
            <div data-id="element-20481">
              <div className="text-sm font-medium text-slate-900" data-id="element-20482">
                {t('userdash.security.passwordAlert')}
              </div>
              <div className="text-xs text-slate-500 mt-0.5" data-id="element-20483">
                {t('userdash.security.passwordAlertDesc')}
              </div>
            </div>
            <Toggle checked={notifyPasswordChange} onChange={setNotifyPasswordChange} data-id="element-20484" />
          </div>
        </div>
      </div>
    </div>;
}